import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SiteNavBar from '../../components/feature/SiteNavBar';
import SiteFooter from '../../components/feature/SiteFooter';
import { propertyData } from '../../mocks/property';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const TIME_SLOTS = ['10:00', '11:00', '13:00', '14:00', '15:00', '16:00'];

function getAvailableDates(): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  let d = new Date(today);
  d.setDate(d.getDate() + 1);
  while (dates.length < 25) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      dates.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

function groupByWeek(dates: Date[]): Date[][] {
  const weeks: Date[][] = [];
  let week: Date[] = [];
  let lastMonday: number | null = null;
  dates.forEach((date) => {
    const mon = new Date(date);
    mon.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    const monTime = mon.getTime();
    if (lastMonday !== monTime) {
      if (week.length) weeks.push(week);
      week = [];
      lastMonday = monTime;
    }
    week.push(date);
  });
  if (week.length) weeks.push(week);
  return weeks;
}

function formatDate(d: Date, locale: string): string {
  return d.toLocaleDateString(locale === 'en' ? 'en-GB' : 'de-CH', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
}

function formatDateFull(d: Date, locale: string): string {
  return d.toLocaleDateString(locale === 'en' ? 'en-GB' : 'de-CH', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

const DAY_LABELS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];
const DAY_LABELS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

export default function TerminPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
  const dayLabels = lang === 'de' ? DAY_LABELS_DE : DAY_LABELS_EN;

  const { ref: refLeft, isVisible: visLeft } = useScrollReveal();
  const { ref: refRight, isVisible: visRight } = useScrollReveal();

  const availableDates = getAvailableDates();
  const weeks = groupByWeek(availableDates);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || charCount > 500) return;
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value.toString()); });
    data.append('datum', formatDateFull(selectedDate, lang));
    data.append('uhrzeit', selectedTime);
    try {
      await fetch('https://readdy.ai/api/form/d7066qdt4s7j0tv5inlg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SiteNavBar />

      {/* Hero */}
      <section className="relative h-72 flex items-end pb-14 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/68170e0959d46d863d26e239d21122b1.jpg"
          alt="Termin Hero"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A] mb-3">The Obsidian Villa</p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-white">{t('appointment.label')}</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-[#FAFAF7]">
        <div className="max-w-2xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="gold-line" />
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('appointment.label')}</p>
            <div className="gold-line" />
          </div>
          <h2 className="font-serif text-4xl font-medium text-[#1C1C1C] mb-5 leading-snug">
            {t('appointment.heading')}
          </h2>
          <p className="font-sans text-sm text-[#6B6460] font-light leading-loose">
            {t('appointment.sub')}
          </p>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-28 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-8">
          {submitted ? (
            <div className="max-w-xl mx-auto text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center border border-[#B59A6A] mx-auto mb-8">
                <i className="ri-calendar-check-line text-2xl text-[#B59A6A]" />
              </div>
              <div className="w-12 h-px bg-[#B59A6A] mx-auto mb-8" />
              <h3 className="font-serif text-3xl text-[#1C1C1C] mb-4">{t('appointment.thanks')}</h3>
              <p className="font-sans text-sm text-[#6B6460] font-light leading-loose max-w-sm mx-auto mb-3">
                {t('appointment.thanksText')}
              </p>
              {selectedDate && selectedTime && (
                <div className="mt-8 border border-[#E5DDD3] bg-white p-6 inline-block">
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#B59A6A] mb-2">{t('appointment.selectedDate')}</p>
                  <p className="font-sans text-sm text-[#1C1C1C] mb-4">{formatDateFull(selectedDate, lang)}</p>
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#B59A6A] mb-2">{t('appointment.selectedTime')}</p>
                  <p className="font-serif text-xl text-[#1C1C1C]">{selectedTime}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
              {/* Calendar + time slots */}
              <div
                ref={refLeft}
                className={`lg:col-span-3 reveal-left ${visLeft ? 'visible' : ''} space-y-10`}
              >
                {/* Date picker */}
                <div className="border border-[#E5DDD3] bg-white p-8">
                  <p className="font-serif text-xl text-[#1C1C1C] mb-1">{t('appointment.selectDate')}</p>
                  <div className="w-8 h-px bg-[#B59A6A] mb-7" />

                  {/* Day header */}
                  <div className="grid grid-cols-5 gap-1 mb-2">
                    {dayLabels.map((d) => (
                      <div key={d} className="text-center font-sans text-[9px] tracking-widest uppercase text-[#BFBAB5] py-1">
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* Date grid by week */}
                  <div className="space-y-1">
                    {weeks.map((week, wi) => {
                      const filledWeek: (Date | null)[] = [null, null, null, null, null];
                      week.forEach((date) => {
                        const dayIndex = (date.getDay() + 6) % 7;
                        if (dayIndex < 5) filledWeek[dayIndex] = date;
                      });
                      return (
                        <div key={wi} className="grid grid-cols-5 gap-1">
                          {filledWeek.map((date, di) => {
                            if (!date) return <div key={di} />;
                            const isSelected = selectedDate?.toDateString() === date.toDateString();
                            return (
                              <button
                                key={di}
                                onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                                className={`py-2.5 px-1 text-center font-sans text-xs transition-all duration-200 cursor-pointer border ${
                                  isSelected
                                    ? 'bg-[#B59A6A] text-white border-[#B59A6A]'
                                    : 'border-[#F0EDE8] text-[#1C1C1C] hover:border-[#B59A6A] hover:text-[#B59A6A]'
                                }`}
                              >
                                <div className="font-sans text-[9px] text-current opacity-60 leading-none mb-1">
                                  {date.toLocaleDateString(lang === 'de' ? 'de-CH' : 'en-GB', { month: 'short' })}
                                </div>
                                <div className="font-medium">
                                  {date.getDate()}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Time slots */}
                <div className={`border border-[#E5DDD3] bg-white p-8 transition-opacity duration-300 ${selectedDate ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                  <p className="font-serif text-xl text-[#1C1C1C] mb-1">{t('appointment.selectTime')}</p>
                  <div className="w-8 h-px bg-[#B59A6A] mb-7" />
                  {selectedDate && (
                    <p className="font-sans text-[11px] text-[#B59A6A] tracking-widest uppercase mb-6">
                      {formatDateFull(selectedDate, lang)}
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    {TIME_SLOTS.map((time) => {
                      const isSel = selectedTime === time;
                      return (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`py-3 font-sans text-sm tracking-widest transition-all duration-200 cursor-pointer border ${
                            isSel
                              ? 'bg-[#B59A6A] text-white border-[#B59A6A]'
                              : 'border-[#E5DDD3] text-[#1C1C1C] hover:border-[#B59A6A] hover:text-[#B59A6A]'
                          }`}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Personal info form */}
              <div
                ref={refRight}
                className={`lg:col-span-2 reveal-right ${visRight ? 'visible' : ''}`}
              >
                <form
                  data-readdy-form
                  id="termin-booking-form"
                  onSubmit={handleSubmit}
                  className="border border-[#E5DDD3] bg-white p-8 space-y-7 sticky top-24"
                >
                  <div>
                    <p className="font-serif text-xl text-[#1C1C1C] mb-1">{t('appointment.yourData')}</p>
                    <div className="w-8 h-px bg-[#B59A6A] mt-3" />
                  </div>

                  {/* Selected summary */}
                  {(selectedDate || selectedTime) && (
                    <div className="bg-[#F5F0E8] px-5 py-4 space-y-2">
                      {selectedDate && (
                        <div className="flex justify-between items-center">
                          <span className="font-sans text-[10px] tracking-widest uppercase text-[#6B6460]">{t('appointment.selectedDate')}</span>
                          <span className="font-sans text-xs text-[#1C1C1C]">{formatDate(selectedDate, lang)}</span>
                        </div>
                      )}
                      {selectedTime && (
                        <div className="flex justify-between items-center">
                          <span className="font-sans text-[10px] tracking-widest uppercase text-[#6B6460]">{t('appointment.selectedTime')}</span>
                          <span className="font-sans text-xs font-medium text-[#B59A6A]">{selectedTime}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('appointment.firstName')}</label>
                      <input
                        type="text" name="vorname" required
                        className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-2.5 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors"
                        placeholder={t('appointment.firstNamePh')}
                      />
                    </div>
                    <div>
                      <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('appointment.lastName')}</label>
                      <input
                        type="text" name="nachname" required
                        className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-2.5 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors"
                        placeholder={t('appointment.lastNamePh')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('appointment.email')}</label>
                    <input
                      type="email" name="email" required
                      className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-2.5 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors"
                      placeholder={t('appointment.emailPh')}
                    />
                  </div>

                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('appointment.phone')}</label>
                    <input
                      type="tel" name="telefon"
                      className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-2.5 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors"
                      placeholder={t('appointment.phonePh')}
                    />
                  </div>

                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('appointment.note')}</label>
                    <textarea
                      name="anmerkungen" maxLength={500} rows={3}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-2.5 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors resize-none"
                      placeholder={t('appointment.notePh')}
                    />
                    <p className={`font-sans text-[10px] mt-1 text-right ${charCount > 450 ? 'text-red-400' : 'text-[#BFBAB5]'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={submitting || !selectedDate || !selectedTime || charCount > 500}
                      className="gold-btn w-full text-center disabled:opacity-40 cursor-pointer"
                    >
                      {submitting ? t('appointment.sending') : t('appointment.send')}
                    </button>
                    <p className="font-sans text-[10px] text-[#BFBAB5] text-center mt-4">
                      {t('appointment.discrete')}
                    </p>
                  </div>
                </form>

                {/* Agent card */}
                <div className="mt-6 border border-[#E5DDD3] bg-white p-6 flex items-center gap-5">
                  <div className="w-14 h-14 rounded-full overflow-hidden shrink-0">
                    <img src={propertyData.agent.photo} alt={propertyData.agent.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="font-serif text-sm font-medium text-[#1C1C1C]">{propertyData.agent.name}</p>
                    <p className="font-sans text-[9px] tracking-widest uppercase text-[#B59A6A] mt-0.5">{propertyData.agent.title}</p>
                    <a href={`tel:${propertyData.agent.phone}`} className="font-sans text-xs text-[#6B6460] hover:text-[#B59A6A] transition-colors mt-1 block cursor-pointer">
                      {propertyData.agent.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
